<template>
  <v-container fluid class="edit-user fill-height">
    <Dialog
      :dialog.sync="deleteUserDialog.show"
      :cardText="deleteUserDialog.cardText"
      :mainBtnText="deleteUserDialog.mainBtnText"
      :secondaryBtnText="deleteUserDialog.secondaryBtnText"
      :alertColor="true"
      @mainBtnCallback="deleteUser"
    />
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      top
      :color="snackbar.color"
      rounded
      centered
    >
      {{ snackbar.text }}
    </v-snackbar>
    <v-layout class="div-center-horizontally">
      <v-flex xs12 sm12>
        <v-container class="medium-div">
          <v-layout row class="header-row">
            <v-flex class="div-center-vertically">
              <template v-if="!permissionsByDeviceDialog">
                <p class="org-name">{{userName}}</p>
              </template>
              <template v-else>
                <p class="org-name"><a class="org-name-clickable" @click="permissionsByDeviceDialog = false">{{userName}}</a> / {{$t('EditUser.permissions')}}</p>
              </template>
            </v-flex>
            <v-flex class="card-actions div-center-vertically div-right-horizontally">
              <p class="user-register-text">{{$t('EditUser.userDetail')}}</p>
              <v-btn @click="closeCard" icon class="close-icon">
                <v-icon color="#000000">close</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
          <v-layout row class="divider-row">
            <v-divider class="divider"/>
          </v-layout>
          <v-layout v-if="permissionsByDeviceDialog" row class="user-content-row">
            <PermissionsByDevice
              :userData.sync="userData"
              :permissionsByDeviceDialog.sync="permissionsByDeviceDialog"
              :devicesPermissionsProp.sync="devicesPermissions"
            />
          </v-layout>
          <v-layout v-else row :class="display === 'xs' ? 'user-content-row-mobile' : 'user-content-row-desktop'">
            <v-flex d-flex xs12 sm3 class="edit-user-div custom-scroll-bar" :class="display === 'xs' ? 'edit-user-div-mobile' : 'edit-user-div-desktop'">
              <v-col>
                <v-form ref="editUserForm">
                  <v-row no-gutters>
                    <v-col cols="12" class="profile-pic-col">
                      <div class="load-img-container">
                        <div class="load-img-btn-div">
                          <v-btn
                            class="load-img-btn"
                            fab
                            small
                            @click="handleFileImport"
                          >
                            <v-icon>
                              photo_camera
                            </v-icon>
                          </v-btn>
                        </div>
                        <input
                          ref="uploader"
                          class="d-none"
                          type="file"
                          accept="image/*"
                          @change="onFileChanged"
                        />
                        <div class="load-img-div">
                          <v-img class="profile-pic" id="profile-pic" width="100%" height="100%" :src="profilePicSrc"/>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row no-gutters class="pt-6">
                    <v-col cols="12">
                      <v-text-field
                        :label="`${$t('EditUser.name')} *`"
                        :title="`${$t('EditUser.name')} *`"
                        v-model="userData.name"
                        outlined
                        background-color="#FFF"
                        color="#0098E1"
                        requiredRules
                        :rules="$ponceConfig.requiredRules"
                        required
                        maxlength="50"
                        hide-details
                        @blur="parseNames"
                      />
                    </v-col>
                  </v-row>
                  <v-row no-gutters class="pt-6">
                    <v-col cols="12">
                      <v-text-field
                        :label="`${$t('EditUser.lastName')} *`"
                        :title="`${$t('EditUser.lastName')} *`"
                        v-model="userData.lastName"
                        outlined
                        background-color="#FFF"
                        color="#0098E1"
                        requiredRules
                        :rules="$ponceConfig.requiredRules"
                        required
                        maxlength="50"
                        hide-details
                        @blur="parseNames"
                      />
                    </v-col>
                  </v-row>
                  <v-row no-gutters class="pt-6">
                    <v-col cols="12">
                      <v-text-field
                        v-if="prevEmail"
                        :label="`${$t('EditUser.email')} *`"
                        :title="`${$t('EditUser.email')} *`"
                        v-model="userData.email"
                        outlined
                        background-color="#FFF"
                        color="#0098E1"
                        :rules="$ponceConfig.emailRules"
                        required
                        maxlength="254"
                        hide-details
                      ></v-text-field>
                      <div v-else class="text-right">
                        <v-textarea
                          class="no-account-label"
                          :label="`${$t('EditUser.email')} *`"
                          :value="$t('EditUser.noAccountUser')"
                          :title="$t('EditUser.noAccountUser')"
                          outlined
                          background-color="#FFF"
                          color="#ffcc00"
                          disabled
                          hide-details
                          auto-grow
                          rows="1"
                          row-height="15"
                        ></v-textarea>
                        <v-btn
                          @click="resendUserInvitationHandler"
                          :loading="resendLoading"
                          medium
                          color="#0098E1"
                          class="text-none px-3"
                          dark
                        >
                          {{$t('EditUser.resendInvitation')}}
                          <v-icon
                            right
                            dark
                            class="mr-0"
                          >
                            forward_to_inbox
                          </v-icon>
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row no-gutters class="pt-6">
                    <v-col cols="12" class="text-right">
                      <v-select
                        :items="roles"
                        item-value="value"
                        :item-text="item => $t(`Roles.${item.value}`)"
                        v-model="userData.role"
                        :label="`${$t('EditUser.role')} *`"
                        :title="`${$t('EditUser.role')} *`"
                        :menu-props="{ bottom: true, offsetY: true }"
                        outlined
                        background-color="#FFF"
                        color="#0098E1"
                        :rules="$ponceConfig.requiredRules"
                        required
                        hide-details
                        @change="setUserImg"
                        :disabled="userData.role === USER_ROLES.OWNER && !$store.getters.user.isAdmin"
                      ></v-select>
                    </v-col>
                  </v-row>
                  <v-row no-gutters>
                    <v-col cols="12">
                      <p class="section-text text-left pt-2">{{$t('GeneralPermissions.permissions')}}</p>
                    </v-col>
                  </v-row>
                  <v-row no-gutters v-for="perm in Object.keys(permissions)" :key="`switch_${perm}`" class="permissions-elements flex-nowrap">
                    <v-switch
                      v-model="permissions[perm]"
                      inset
                      color="#00D166"
                      :disabled="userData.permissionsCustomized"
                    />
                    <p class="mb-0 text-start align-self-center">{{$t(`UsersPermissions.${perm}`)}}</p>
                  </v-row>
                  <v-row no-gutters>
                    <v-col cols="12">
                      <p class="section-text text-left pt-2">{{$t('GeneralPermissions.channels')}}</p>
                    </v-col>
                  </v-row>
                  <v-row class="permissions-elements flex-nowrap mt-0">
                    <v-switch
                      @click="changeChannel('push')"
                      v-model="pushChannel"
                      inset
                      color="#00D166"
                    />
                    <p class="mb-0 text-start align-self-center">{{$t('GeneralPermissions.push')}}</p>
                  </v-row>
                  <v-row class="permissions-elements flex-nowrap mt-0">
                    <v-switch
                      @click="changeChannel('whatsapp')"
                      v-model="whatsappChannel"
                      inset
                      color="#00D166"
                    />
                    <p class="mb-0 text-start align-self-center">{{$t('GeneralPermissions.whatsapp')}}</p>
                  </v-row>
                  <v-row class="permissions-elements flex-nowrap mt-0">
                    <v-switch
                      @click="changeChannel('sms')"
                      v-model="smsChannel"
                      inset
                      color="#00D166"
                    />
                    <p class="mb-0 text-start align-self-center">{{$t('GeneralPermissions.sms')}}</p>
                  </v-row>
                  <v-row no-gutters class="pt-3 pb-5">
                    <v-col cols="12">
                      <VuePhoneNumberInput
                        v-model="phoneNumberInput"
                        color="#0098E1"
                        size="md"
                        :default-country-code="defaultCountryCode()"
                        :translations="phoneInputTranslations"
                        @update="setPhoneNumber"
                      />
                    </v-col>
                  </v-row>
                </v-form>
              </v-col>
            </v-flex>
            <v-flex xs12 sm9 class="edit-permissions-div" :class="display === 'xs' ? 'edit-permissions-div-mobile' : 'edit-permissions-div-desktop'">
              <v-col cols="12" class="full-height pt-0">
                <v-row no-gutters style="justify-content: right">
                  <v-btn
                    @click="openPermissionsByDevice"
                    text
                    color="#0098E1"
                    class="text-none text-body-2 font-weight-bold my-0 mx-0"
                  >{{$t('EditUser.seePermisions')}}</v-btn>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" sm="6">
                    <p class="title-text">{{$t('EditUser.assignedDevices')}}</p>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-row no-gutters style="justify-content: end">
                      <v-switch
                        class="text-end mt-0"
                        v-model="selectAll"
                        inset
                        color="#00D166"
                        :label="$t('EditUser.selectAll')"
                      />
                    </v-row>
                  </v-col>
                </v-row>
                <v-row no-gutters class="assigned-devices-div" :class="display === 'xs' ? 'assigned-devices-div-mobile' : 'assigned-devices-div-desktop'">
                  <v-flex v-for="device in assignedDevices" :key="device.name" xs6 sm4 class="px-2 fit-content">
                    <v-row class="device-elements flex-nowrap">
                      <v-switch
                        :input-value="device.isAssigned"
                        @change="updateDeviceAssignment(device, $event)"
                        inset
                        color="#00D166"
                      />
                      <p class="mb-0 text-start align-self-center">{{device.campaign.entityName}}</p>
                    </v-row>
                  </v-flex>
                </v-row>
                <v-row>
                  <v-col class="d-flex flex-column align-center">
                    <v-btn
                      @click="deleteUserBtnHandler"
                      medium
                      :disabled="loadingSave"
                      :loading="loadingDelete"
                      color="#F93A2F"
                      class="text-none text-h6 font-weight-light"
                      dark
                    >{{$t('EditUser.deleteUser')}}</v-btn>
                  </v-col>
                  <v-col class="d-flex flex-column align-center">
                    <v-btn
                      @click="saverUserData"
                      :disabled="loadingDelete"
                      :loading="loadingSave"
                      medium
                      color="#0098E1"
                      class="text-none text-h6 font-weight-light"
                      dark
                    >{{$t('EditUser.save')}}</v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-flex>
          </v-layout>
        </v-container>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import PermissionsByDevice from './AddUser/PermissionsByDevice'
import Dialog from '../Shared/Dialog.vue'
import {deleteUser} from '../../lib/crudOperations/delete'
import Snackbar from '../Shared/Snackbar.vue'
import {
  PROFILE_PIC_FILE_SIZE_LIMIT,
  ALLOWED_IMAGES_FILE_TYPES,
  ALLOWED_IMAGES_FILE_EXTENSIONS,
  WEBHOOKS_PATHS,
  DEFAULT_PERMISSIONS_BY_ROLE,
  EVENTS_TO_TRACK,
  USER_ROLES
} from '../../constants/const'
import { updateUser, resendUserInvitation } from '../../lib/crudOperations/write'
import _get from 'lodash/get'
import _capitalize from 'lodash/capitalize'
import _omit from 'lodash/omit'
import { auth } from '../../plugins/firebase'
import parsePhoneNumber from 'libphonenumber-js'
import { trackEvent } from '../../lib/track-events'

export default {
  components: {
    VuePhoneNumberInput,
    PermissionsByDevice,
    Dialog,
    Snackbar
  },
  data () {
    const email = this.$route.params.user ? this.$route.params.user.email : ''
    return {
      organization: this.$store.getters.userOrganization.name,
      userData: {
        id: this.$route.params.user ? this.$route.params.user.id : '',
        name: this.$route.params.user ? this.$route.params.user.name : '',
        lastName: this.$route.params.user ? this.$route.params.user.lastName : '',
        phone: this.$route.params.user ? this.$route.params.user.phone : '',
        email,
        role: this.$route.params.user ? this.$route.params.user.role : '',
        editDataPermission: this.$route.params.user && this.$route.params.user.editDataPermission ? this.$route.params.user.editDataPermission : false,
        photoUrl: this.$route.params.user ? this.$route.params.user.photoUrl : '',
        profilePic: this.$route.params.user ? this.$route.params.user.profilePic : '',
        devices: this.$route.params.user ? this.$route.params.user.devices : [],
        channels: this.$route.params.user ? [...this.$route.params.user.channels] : [],
        settings: _get(this.$route.params, 'user.settings', {}),
        reports: _get(this.$route.params, 'user.reports', false)
      },
      prevEmail: email,
      permissionsByDeviceDialog: false,
      profilePicSrc: null,
      assignedDevices: this.$store.getters.devices,
      roles: this.getRoles(),
      phoneNumberInput: this.$route.params.user ? this.$route.params.user.phone : '',
      phoneInputTranslations: {
        countrySelectorLabel: this.$t('EditUser.countryCode'),
        phoneNumberLabel: `${this.$t('EditUser.phoneNumber')} *`,
        example: this.$t('EditUser.phoneExample')
      },
      isPhoneValid: false,
      deleteUserDialog: {
        show: false,
        cardText: this.$t('EditUser.deleteUserCardText', [this.$route.params.user ? this.$route.params.user.name : '']),
        mainBtnText: this.$t('EditUser.deleteUserMainBtnText'),
        secondaryBtnText: this.$t('EditUser.deleteUserSecondaryBtnText')
      },
      loadingDelete: false,
      loadingSave: false,
      snackbar: {
        show: false,
        text: '',
        color: '',
        timeout: 3000
      },
      selectAll: false,
      whatsappChannel: this.$route.params.user.channels.includes('whatsapp'),
      smsChannel: this.$route.params.user.channels.includes('sms'),
      pushChannel: this.$route.params.user.channels.includes('push'),
      startHour: _get(this.$route.params.user, 'settings.notificationsPeriod.startHour', '00:00'),
      endHour: _get(this.$route.params.user, 'settings.notificationsPeriod.endHour', '23:59'),
      permissions: {
        reports: this.$route.params.user.reports || false,
        editDataPermission: this.$route.params.user.editDataPermission || false,
        audits: this.$route.params.user.audits || false
      },
      userName: `${this.$route.params.user.name} ${this.$route.params.user.lastName}`,
      resendLoading: false,
      devicesPermissions: [],
      USER_ROLES
    }
  },
  created () {
    this.setUserImg()
    this.assignedDevices.forEach(device => {
      const deviceFind = this.userData.devices.find(elem => elem === device.id)
      device.isAssigned = !!deviceFind
    })
    this.setInitialPermissionsByDevice()
  },
  methods: {
    setUserImg () {
      if (this.userData.photoUrl) {
        this.profilePicSrc = this.userData.profilePic
      } else {
        switch (this.userData.role) {
          case 'admin':
          case 'owner':
            this.profilePicSrc = require('../..//assets/owner.png')
            break
          case 'manager':
            this.profilePicSrc = require('../..//assets/manager.png')
            break
          case 'fieldman':
            this.profilePicSrc = require('../..//assets/fieldman.png')
            break
          case 'farmhand':
            this.profilePicSrc = require('../..//assets/farmhand.png')
            break
        }
      }
    },
    getRoles () {
      const roles = [...this.$ponceConfig.roles]
      if (this.$store.getters.user.isAdmin) {
        roles.push({value: 'admin'})
      }
      return roles
    },
    setPhoneNumber (input) {
      this.isPhoneValid = input.isValid
      if (input && input.formattedNumber) {
        this.userData.phone = input.formattedNumber
      } else {
        this.userData.phone = ''
      }
    },
    handleFileImport () {
      this.$refs.uploader.click()
    },
    onFileChanged (e) {
      const file = e.target.files[0]
      if (file.size <= PROFILE_PIC_FILE_SIZE_LIMIT * 1024 * 1024 && ALLOWED_IMAGES_FILE_TYPES.includes(file.type)) {
        const imageSrc = URL.createObjectURL(file)
        this.profilePicSrc = imageSrc
        this.userData.profilePic = imageSrc
        this.userData.photoUrl = imageSrc
        this.userData.profilePicFile = file
        this.$emit('update:userData', {
          ...this.userData,
          profilePic: imageSrc,
          photoUrl: imageSrc
        })
      } else {
        this.snackbar.show = true
        this.snackbar.color = '#F93A2F'
        if (!ALLOWED_IMAGES_FILE_TYPES.includes(file.type)) {
          this.snackbar.text = this.$t('EditUser.allowedFileTypes', [ALLOWED_IMAGES_FILE_EXTENSIONS])
        } else {
          this.snackbar.text = this.$t('EditUser.fileSizeLimit', [PROFILE_PIC_FILE_SIZE_LIMIT])
        }
      }
    },
    closeCard () {
      this.$router.push({name: 'OrganizationHome'})
    },
    deleteUserBtnHandler () {
      trackEvent(EVENTS_TO_TRACK.CLICK_DELETE_USER_BUTTON, { userId: this.userData.id, userName: this.userData.name })
      this.deleteUserDialog.show = true
    },
    async logoutHandler () {
      this.$store.dispatch('logout')
      await auth.signOut()
      this.$router.push({name: 'Signin'})
    },
    async deleteUser () {
      trackEvent(EVENTS_TO_TRACK.CLICK_CONFIRM_DELETE_USER_BUTTON, { userId: this.userData.id, userName: this.userData.name })
      try {
        this.loadingDelete = true
        await deleteUser(this.userData.id)
        this.$root.snack.show({text: this.$t('UI.deleteSuccess')})
        if (this.userData.id === this.$store.getters.user.id) {
          await this.logoutHandler()
        } else {
          this.$router.push({name: 'OrganizationHome'})
        }
      } catch (err) {
        this.$root.snack.show({text: this.$t('UI.deleteError'), error: true})
      } finally {
        this.loadingDelete = false
      }
    },
    async saverUserData () {
      if (this.$refs.editUserForm.validate()) {
        try {
          this.loadingSave = true
          if (this.userData.email !== this.$route.params.user.email) {
            const signInMethodsForEmail = await auth.fetchSignInMethodsForEmail(this.userData.email)
            if (signInMethodsForEmail.length > 0) {
              this.$root.snack.show({text: this.$t('Config.emailAlreadyInUse'), error: true})
              return
            }
          }
          if (this.userData.phone !== this.$route.params.user.phone) {
            const response = await fetch(`${process.env.WEBHOOKS_URL}${WEBHOOKS_PATHS.PHONE_EXISTS}?number=${encodeURIComponent(this.userData.phone)}`, {
              headers: {
                authorization: process.env.WEBHOOKS_BASIC_TOKEN
              }
            })
            const resData = await response.json()
            if (resData.exists === true) {
              this.$root.snack.show({text: this.$t('UI.phoneAlreadyExists'), error: true})
              return
            }
          }
          this.userData.audits = this.permissions.audits
          this.userData.reports = this.permissions.reports
          this.userData.editDataPermission = this.permissions.editDataPermission
          trackEvent(EVENTS_TO_TRACK.CLICK_SAVE_USER_BUTTON, _omit({...this.userData}, ['profilePic', 'profilePicFile']))
          await updateUser(this.userData, this.assignedDevices, this.devicesPermissions)
          this.$root.snack.show({text: this.$t('UI.registerSuccess')})

          if (this.userData.id === this.$store.getters.user.id && this.prevEmail !== this.userData.email) { // Logout when user modifies its own email
            await this.logoutHandler()
          } else {
            this.$router.push({name: 'OrganizationHome'})
          }
        } catch (error) {
          this.$root.snack.show({text: this.$t('UI.registerError'), error: true})
        } finally {
          this.loadingSave = false
        }
      } else {
        this.$root.snack.show({text: this.$t('Config.fillRequiredFields'), error: true})
      }
    },
    async resendUserInvitationHandler () {
      try {
        this.resendLoading = true
        await resendUserInvitation(this.userData.id)
        this.$root.snack.show({text: this.$t('EditUser.invitationSent')})
      } catch (error) {
        this.$root.snack.show({text: this.$t('UI.serverError'), error: true})
      } finally {
        this.resendLoading = false
      }
    },
    parseNames () {
      this.userData.name = this.userData.name && this.userData.name.replace(/\s+/g, ' ').split(' ').map(string => _capitalize(string)).join(' ')
      this.userData.lastName = this.userData.lastName && this.userData.lastName.replace(/\s+/g, ' ').split(' ').map(string => _capitalize(string)).join(' ')
    },
    changeChannel (channel) {
      const newChannels = this.userData.channels
      let channelIndex
      let channelFlag
      switch (channel) {
        case 'whatsapp':
          channelFlag = this.whatsappChannel
          break
        case 'sms':
          channelFlag = this.smsChannel
          break
        case 'push':
          channelFlag = this.pushChannel
          break
      }
      if (channelFlag && !this.userData.channels.includes(channel)) {
        newChannels.push(channel)
        this.userData.channels = newChannels
      } else if (!channelFlag && this.userData.channels.includes(channel)) {
        channelIndex = this.userData.channels.findIndex(elem => elem === channel)
        newChannels.splice(channelIndex, 1)
        this.userData.channels = newChannels
      }
    },
    defaultCountryCode () {
      const phone = _get(this.$route.params, 'user.phone')
      if (phone) {
        const parsedPhoneNumber = parsePhoneNumber(phone)
        return parsedPhoneNumber.country
      }
      return 'AR'
    },
    openPermissionsByDevice () {
      trackEvent(EVENTS_TO_TRACK.CLICK_OPEN_PERMISSIONS_BY_DEVICE_BUTTON, {userId: this.userData.id, userName: this.userData.name})
      this.permissionsByDeviceDialog = true
    },
    setInitialPermissionsByDevice () {
      this.syncDevicesPermissions()
    },
    syncDevicesPermissions () {
      // Sync devicesPermissions with assignedDevices
      const devicesPermissions = []
      this.assignedDevices.filter(device => device.isAssigned).forEach(device => {
        const storeDevice = this.$store.getters.devices.find(elem => elem.id === device.id)
        if (!storeDevice) return

        const user = storeDevice.notifications.find(elem => elem.id === this.userData.id)
        if (user && user.permissions) {
          // User already has permissions, use them
          devicesPermissions.push({
            id: device.id,
            name: device.campaign.entityName,
            permissions: { ...user.permissions }
          })
        } else {
          // User doesn't have permissions, use default permissions by role
          const permissionsByRole = DEFAULT_PERMISSIONS_BY_ROLE[this.userData.role]
          devicesPermissions.push({
            id: device.id,
            name: device.campaign.entityName,
            permissions: {
              failure: !!permissionsByRole.includes('failure'),
              pressure: !!permissionsByRole.includes('pressure'),
              off: !!permissionsByRole.includes('off'),
              position: !!permissionsByRole.includes('position'),
              irrigationOrders: !!permissionsByRole.includes('irrigationOrders'),
            }
          })
        }
      })
      this.devicesPermissions = devicesPermissions
    },
    updateDeviceAssignment (device, value) {
      this.$set(device, 'isAssigned', value)
      // Sync permissions after assignment change
      this.syncDevicesPermissions()
    }
  },
  computed: {
    display () {
      return this.$vuetify.breakpoint.name
    }
  },
  watch: {
    selectAll (val) {
      this.assignedDevices = this.assignedDevices.map(device => { return {...device, isAssigned: val} })
      // Sync permissions after select all change
      this.syncDevicesPermissions()
    }
  }
}
</script>



// WEBPACK FOOTER //
// EditUser.vue?3f82d50e